import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, mergeProps as _mergeProps, resolveComponent as _resolveComponent, withKeys as _withKeys, createSlots as _createSlots, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, ref, watch } from "vue";
import { ArtifactSchema, ArtifactTypeSchema } from "@/types";
import { filterArtifacts } from "@/util";
import {
  artifactStore,
  chatApiStore,
  chatStore,
  layoutStore,
  searchApiStore,
  searchStore,
  selectionStore,
  timStore,
} from "@/hooks";
import { Typography, Icon, FlexBox, TypeButtons } from "@/components/common";
import SearchOption from "./SearchOption.vue";
import SearchInputs from "./SearchInputs.vue";


/**
 * Displays a search prompt that allows the user to search for artifacts.
 */
const __default__ = {
  name: "SearchPromptInput",
};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const searchOptions = ref<ArtifactSchema[] | string[]>([]);
const hiddenTypes = ref<string[]>([]);
const currentText = ref("");

/**
 * Returns the display text for how many matches there are.
 */
const matchText = computed(() => {
  const count = searchOptions.value.length;

  return count === 1 ? "1 Match" : `${count} Matches`;
});

/**
 * Clears options if an item has been selected.
 */
function clearOptions(): void {
  searchOptions.value =
    searchStore.selectionCount > 0 ? [] : searchOptions.value;
}

/**
 * Filters the options based on the search text.
 * @param search - The search text.
 * @param update - A function called to update the options.
 */
function filterOptions(search: string, update: (fn: () => void) => void) {
  searchStore.searchText = search;
  currentText.value = search;

  update(() => {
    if (
      searchStore.basicSearchMode ||
      (searchStore.artifactLikeMode && search !== "")
    ) {
      // Basic search always shows artifacts.
      // Artifact-like search only shows artifacts when there is search text.
      searchOptions.value = artifactStore.currentArtifacts.filter(
        (artifact) =>
          !hiddenTypes.value.includes(artifact.type) &&
          filterArtifacts(artifact, search)
      );
    } else if (search === "" || searchStore.selectionCount > 0) {
      // Non-basic search shows no artifacts when there is no search text.
      searchOptions.value = [];
    } else if (searchStore.artifactTypeMode) {
      // Artifact type search shows artifact types when there is search text.
      const lowercaseSearch = search.toLowerCase();

      searchOptions.value = timStore.typeNames.filter((type) =>
        type.toLowerCase().includes(lowercaseSearch)
      );
    } else {
      searchOptions.value = [];
    }
  });
}

/**
 * Toggles whether a type is visible in the artifact list.
 * @param option - The type to toggle.
 * @param allOptions - All possible types.
 */
function handleTypeChange(
  option: ArtifactTypeSchema,
  allOptions: ArtifactTypeSchema[]
): void {
  if (hiddenTypes.value.length === 0) {
    hiddenTypes.value = allOptions
      .map((type) => type.name)
      .filter((type) => type !== option.name);
  } else if (hiddenTypes.value.find((type) => type === option.name)) {
    hiddenTypes.value = hiddenTypes.value.filter(
      (type) => type !== option.name
    );
  } else {
    hiddenTypes.value.push(option.name);
  }

  if (hiddenTypes.value.length === allOptions.length) {
    hiddenTypes.value = [];
  }

  searchOptions.value = artifactStore.currentArtifacts.filter(
    (artifact) => !hiddenTypes.value.includes(artifact.type)
  );
}

/**
 * Handles a search in chat mode, creating a new chat.
 */
function handleChat() {
  if (!searchStore.chatSearchMode) return;

  chatStore.addChat();
  chatApiStore.handleSendChatMessage(chatStore.currentChat, currentText.value);
  layoutStore.mode = "chat";
}

/**
 * When the search changes in basic search mode,
 * highlight the selected artifact.
 */
watch(
  () => searchStore.searchItems,
  (artifacts: ArtifactSchema[] | string[] | null) => {
    if (!searchStore.basicSearchMode) return;

    if (
      !!artifacts &&
      artifacts.length === 1 &&
      typeof artifacts[0] === "object"
    ) {
      selectionStore.selectArtifact(artifacts[0].id);
    } else {
      selectionStore.clearSelections();
    }
  }
);

watch(
  () => artifactStore.selectedArtifact,
  (artifact) => {
    if (
      !searchStore.basicSearchMode ||
      !artifact ||
      searchStore.searchItems?.[0] === artifact
    )
      return;

    searchStore.searchItems = [artifact];
  }
);

return (_ctx: any,_cache: any) => {
  const _component_q_select = _resolveComponent("q-select")!

  return (_openBlock(), _createBlock(_component_q_select, {
    modelValue: _unref(searchStore).searchItems,
    "onUpdate:modelValue": [
      _cache[0] || (_cache[0] = ($event: any) => ((_unref(searchStore).searchItems) = $event)),
      clearOptions
    ],
    outlined: "",
    "use-input": "",
    clearable: "",
    multiple: "",
    label: _unref(searchStore).mode.placeholder,
    options: searchOptions.value,
    "option-label": _unref(searchStore).artifactLikeMode ? 'name' : undefined,
    "option-value": _unref(searchStore).artifactLikeMode ? 'id' : undefined,
    "input-debounce": "0",
    class: "nav-input nav-search-prompt nav-multi-input-right",
    "menu-anchor": "bottom middle",
    "menu-self": "top middle",
    "data-cy": "input-nav-artifact-search",
    onFilter: filterOptions,
    onKeydown: _withKeys(handleChat, ["enter"])
  }, _createSlots({
    append: _withCtx(() => [
      _createVNode(_unref(Icon), { variant: "search" })
    ]),
    "before-options": _withCtx(() => [
      _createVNode(_unref(FlexBox), {
        l: "2",
        y: "2",
        justify: "between",
        align: "center"
      }, {
        default: _withCtx(() => [
          _createVNode(_unref(Typography), {
            variant: "caption",
            value: matchText.value,
            "data-cy": "text-artifact-search-count"
          }, null, 8, ["value"]),
          _createVNode(_unref(TypeButtons), {
            "default-visible": "",
            "hidden-types": hiddenTypes.value,
            onClick: handleTypeChange
          }, null, 8, ["hidden-types"])
        ]),
        _: 1
      })
    ]),
    option: _withCtx(({ opt, itemProps }) => [
      _createVNode(SearchOption, _mergeProps(itemProps, { option: opt }), null, 16, ["option"])
    ]),
    _: 2
  }, [
    (!_unref(searchStore).basicSearchMode && !_unref(searchStore).chatSearchMode)
      ? {
          name: "no-option",
          fn: _withCtx(() => [
            _createVNode(SearchInputs, {
              onSubmit: _unref(searchApiStore).handleSearch
            }, null, 8, ["onSubmit"])
          ]),
          key: "0"
        }
      : undefined
  ]), 1032, ["modelValue", "label", "options", "option-label", "option-value"]))
}
}

})